import { Spacing } from '@persona-id/design-tokens';
import { PlayIcon } from '@persona-id/design-tokens/icons';
import { capitalize } from 'lodash';
import { Fragment } from 'react';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import Divider from 'dashboard/components/Divider';
import InputWithHoverEditButtonField from 'dashboard/components/FormControls/InputWithHoverEditButtonField';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import { Container, Header } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, { TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import {
  InsightQueriesPreviewResult,
  InsightQueriesPreviewResults,
  InsightQueriesSchema,
  RecommendedQueries,
  SensitiveDataSchema,
} from 'dashboard/models/insight-queries';
import { ExpandDataModal } from 'dashboard/views/DashboardInsightQueryToolView/ExpandDataModal';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';
import { PrefixedIdValidator } from 'lib/utils/prefixedIdValidator';

export interface DashboardInsightQueryToolViewProps {
  code: string;
  handleChangeQueryName: (value: string) => void;
  handleDownloadCsv: () => void;
  showTableLoader: boolean;
  loading: boolean;
  onCodeChange: (value: string) => void;
  onExport: () => void;
  onPreview: () => void;
  onSave: () => void;
  expandData: () => void;
  isExportsEnabled: boolean;
  isLoadingExpandData: boolean;
  expandedTablesAvailable: boolean;
  isSensitiveDataModeEnabled: boolean;
  previewResults: InsightQueriesPreviewResults;
  queryName: string;
  sqlError: string | null;
}

const ButtonGroup = styled('div')`
  display: flex;
  grid-column-start: 4;
  justify-content: flex-end;
  padding-right: ${Spacing[4]};

  > * + * {
    margin-left: ${Spacing[3]};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  height: 100%;
`;

const EditorSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SidePanel = styled.div`
  width: 450px;
  display: flex;
  border-left: 1px solid #c9cad3;
  overflow-y: auto;
`;

const AttributesGridWrapper = styled(AttributeGrid)`
  padding: ${Spacing[2]} ${Spacing[0]} ${Spacing[2]} ${Spacing[2]};
`;

const CodeEditorWrapper = styled.div`
  height: 500px;
`;

const StyledCardBody = styled(Card.Body)`
  display: flex;
  justify-content: space-between;
`;

const TableWrapper = styled.div<{ isLoading: boolean }>`
  min-height: 40vh;
`;

const StyledCard = styled(Card)`
  margin-bottom: ${Spacing[0]};
`;

const DashboardInsightQueryToolView = ({
  code,
  handleChangeQueryName,
  handleDownloadCsv,
  showTableLoader,
  loading,
  onCodeChange,
  expandData,
  onExport,
  onPreview,
  onSave,
  previewResults,
  queryName,
  isExportsEnabled,
  isLoadingExpandData,
  expandedTablesAvailable,
  isSensitiveDataModeEnabled,
}: DashboardInsightQueryToolViewProps) => {
  const [isModalOpen, { enable: showModal, disable: hideModal }] = useToggle();
  const columnNameHeaders = previewResults.length > 0 ? Object.keys(previewResults[0]) : [];
  const displayQueryName = queryName ? queryName : 'Untitled Query';

  const exportActions = [
    <Button
      id='insight-queries-view__download-csv-button'
      key='insight-queries-view__download-csv-button'
      variant='basic'
      onClick={() => handleDownloadCsv()}
      disabled={previewResults.length === 0}
    >
      Download CSV
    </Button>,
  ];

  if (isExportsEnabled) {
    exportActions.push(
      <Button
        id='insight-queries-view__export-button'
        key='insight-queries-view__export-button'
        variant='basic'
        onClick={() => onExport()}
        disabled={!code || loading}
      >
        Export
      </Button>,
    );
  }

  const renderTableCell = (value: string, index: number): JSX.Element => {
    const routeMap: Partial<Record<ApiModel, string>> = {
      [ApiModel.Inquiry]: 'inquiries',
      [ApiModel.Account]: 'accounts',
      [ApiModel.Verification]: 'verifications',
      [ApiModel.Transaction]: 'transactions',
      [ApiModel.Case]: 'cases',
      [ApiModel.Tag]: 'tags',
    };

    const objectType = PrefixedIdValidator.getObjectType(value);

    if (objectType != null && routeMap[objectType] != null) {
      return (
        <Link key={index} target='_blank' to={`/dashboard/${routeMap[objectType]}/${value}`}>
          {value}
        </Link>
      );
    }
    return <>{value}</>;
  };

  return (
    <Container>
      <InactiveEnvironmentBanner />
      <Header
        title='Insight Query Editor'
        append={
          <Tooltip
            trigger={
              <div style={{ marginLeft: '8px' }}>
                <Sticker variant='yellow'>BETA</Sticker>
              </div>
            }
          >
            This feature is in beta. Please don&apos;t rely on it for critical checks or automation.
            Please share your feedback with us.
          </Tooltip>
        }
        prepend={
          <>
            <NavBack to='/dashboard/insight-queries' />
          </>
        }
      >
        <ButtonGroup>
          {isSensitiveDataModeEnabled && (
            <Button
              id='insight-query-view__sensitive-data-button'
              width='auto'
              size='md'
              variant='danger'
              onClick={showModal}
              disabled={false}
            >
              Sensitive Data Mode
            </Button>
          )}
          <Button
            id='insight-queries-view__create-button'
            name='create-insight-query.button'
            variant='basic'
            onClick={onSave}
            isPending={loading}
            disabled={!queryName || !code || loading}
          >
            Save
          </Button>
        </ButtonGroup>
      </Header>
      <ModalWrapper isOpen={isModalOpen} shouldCloseOnEsc shouldCloseOnOverlayClick>
        <ExpandDataModal expandData={expandData} onClose={hideModal} />
      </ModalWrapper>
      <ContentWrapper>
        <EditorSection>
          <StyledCardBody>
            <InputWithHoverEditButtonField
              fieldName='insight-query-tool__query-name'
              displayElement={<Paragraph size='md' />}
              displayPlaceholder={displayQueryName}
              iconVisible={false}
              placeholder={displayQueryName}
              onBlur={(value: string) => handleChangeQueryName(value)}
              disabled={loading}
            />
            <Button
              id='insight-query-view__run-query-button'
              width='auto'
              size='md'
              prefixIcon={<PlayIcon />}
              onClick={onPreview}
              disabled={!code || loading}
            >
              Run
            </Button>
          </StyledCardBody>
          <CodeEditorWrapper>
            <CodeEditor
              code={code}
              language='sql'
              placeholder='select * from inquiries'
              editable={!loading}
              schemas={{
                sql: {
                  tables: InsightQueriesSchema || [],
                },
              }}
              onCommandEnter={onPreview}
              onChange={onCodeChange}
            />
          </CodeEditorWrapper>
          <StyledCard>
            <Card.Body>
              <Card.Title actions={exportActions}>Results</Card.Title>
              <TableWrapper isLoading={showTableLoader}>
                <TableLayout
                  isEmpty={previewResults.length === 0}
                  isLoading={showTableLoader}
                  showHeader={previewResults.length > 0}
                  containerMaxHeight={500}
                  headerTR={
                    <>
                      <TableTH key='row-count-head'></TableTH>
                      {columnNameHeaders.map((key, i) => (
                        <TableTH key={`${key}-${i}`}>{key.toUpperCase()}</TableTH>
                      ))}
                    </>
                  }
                  nullState={
                    <TableNullState dataTestBlock='insight-queries'>
                      <Card.Title>Unable to preview results.</Card.Title>
                      <Paragraph size='sm'>Edit and run the query to preview the results</Paragraph>
                    </TableNullState>
                  }
                >
                  {previewResults.map((row: InsightQueriesPreviewResult, i: number) => (
                    <TableTR key={`insight-queries-view-result-row-${i}`}>
                      <TableTD key='row-count' size='sm'>
                        {i + 1}
                      </TableTD>
                      {columnNameHeaders.map((key: string, j: number) => (
                        <TableTD key={`${key}-${j}`}>
                          {renderTableCell(row[key] ?? 'NULL', j)}
                        </TableTD>
                      ))}
                    </TableTR>
                  ))}
                </TableLayout>
              </TableWrapper>
            </Card.Body>
          </StyledCard>
        </EditorSection>
        <SidePanel>
          <StyledCard>
            <Card.Body>
              <Card.Title>Recommended questions</Card.Title>
              {RecommendedQueries.map(({ product, queries }, i: number) => (
                <div key={product}>
                  <Card.Subtitle>{product}</Card.Subtitle>
                  {queries.map(({ title, query }, j) => (
                    <div key={title}>
                      <Paragraph
                        id={title}
                        onClick={() => onCodeChange(query)}
                        style={{ cursor: 'pointer', color: 'blue' }}
                      >
                        {title}
                      </Paragraph>
                      {j !== queries.length - 1 && <Spacer height={1} />}
                    </div>
                  ))}
                  {i !== queries.length - 1 && <Spacer height={1} />}
                </div>
              ))}
              <Divider full={true} />
              <Card.Title>Table Schemas</Card.Title>
              {InsightQueriesSchema.map(({ name: tableName, columns }) => (
                <Fragment key={`dashboard-insight-query-${tableName}`}>
                  <Accordion
                    title={capitalize(tableName)}
                    alignCaret='center'
                    defaultExpanded={false}
                  >
                    <AttributesGridWrapper horizontal={true}>
                      {columns.map(({ name: columnName, type }) => (
                        <Attribute
                          horizontal={true}
                          key={`${tableName}-${columnName}-attribute}`}
                          label={columnName.toUpperCase()}
                          value={type.toUpperCase()}
                        />
                      ))}
                    </AttributesGridWrapper>
                  </Accordion>
                  <Spacer height={1} />
                </Fragment>
              ))}
              {!isLoadingExpandData && expandedTablesAvailable && (
                <>
                  <Card.Title>Sensitive Tables</Card.Title>
                  {SensitiveDataSchema.map(({ name: tableName, columns }) => (
                    <Accordion
                      key={`dashboard-insight-query-${tableName}-columns`}
                      title={tableName}
                      alignCaret='center'
                      defaultExpanded={false}
                    >
                      <AttributesGridWrapper horizontal={true}>
                        {columns.map(({ name: columnName, type }) => (
                          <Attribute
                            horizontal={true}
                            key={`${tableName}-${columnName}-attribute}`}
                            label={columnName}
                            value={type}
                          />
                        ))}
                      </AttributesGridWrapper>
                    </Accordion>
                  ))}
                  <Spacer height={1} />
                </>
              )}
            </Card.Body>
          </StyledCard>
        </SidePanel>
      </ContentWrapper>
    </Container>
  );
};

export default DashboardInsightQueryToolView;
